/**
 * Inline HTML for the JSX renderer iframe, served via srcdoc so the iframe
 * gets an opaque origin and cannot access parent cookies/localStorage.
 *
 * TODO: Move to a separate origin (e.g. sandbox.oilytics.co) for defense-in-depth.
 * srcdoc with allow-scripts (no allow-same-origin) is safe, but a dedicated
 * origin adds CSP isolation and makes auditing easier.
 */
export const RENDERER_SHELL_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>JSX Renderer Shell</title>

<!--
  Import map for ESM module resolution. Sucrase's "imports" transform converts
  \`import X from 'react'\` into \`require('react')\`, which we shim below to resolve
  against these entries. esm.sh serves ES modules with proper export handling.
-->
<script type="importmap">
{
  "imports": {
    "react": "https://esm.sh/react@18",
    "react/": "https://esm.sh/react@18/",
    "react-dom": "https://esm.sh/react-dom@18",
    "react-dom/": "https://esm.sh/react-dom@18/",
    "react-dom/client": "https://esm.sh/react-dom@18/client",
    "recharts": "https://esm.sh/recharts@2.12.7?external=react,react-dom",
    "lodash": "https://esm.sh/lodash@4",
    "mathjs": "https://esm.sh/mathjs@13",
    "lucide-react": "https://esm.sh/lucide-react@0.383.0?external=react"
  }
}
</script>

<!-- Tailwind CSS for utility classes -->
<script src="https://cdn.tailwindcss.com/3.4.17"></script>
<script>
  // Configure Tailwind with the same theme tokens used by the parent app's shadcn/ui
  tailwind.config = {
    theme: {
      extend: {
        colors: {
          border: 'hsl(214.3, 31.8%, 91.4%)',
          input: 'hsl(214.3, 31.8%, 91.4%)',
          ring: 'hsl(222.2, 84%, 4.9%)',
          background: 'hsl(0, 0%, 100%)',
          foreground: 'hsl(222.2, 84%, 4.9%)',
          primary: { DEFAULT: 'hsl(222.2, 47.4%, 11.2%)', foreground: 'hsl(210, 40%, 98%)' },
          secondary: { DEFAULT: 'hsl(210, 40%, 96.1%)', foreground: 'hsl(222.2, 47.4%, 11.2%)' },
          destructive: { DEFAULT: 'hsl(0, 84.2%, 60.2%)', foreground: 'hsl(210, 40%, 98%)' },
          muted: { DEFAULT: 'hsl(210, 40%, 96.1%)', foreground: 'hsl(215.4, 16.3%, 46.9%)' },
          accent: { DEFAULT: 'hsl(210, 40%, 96.1%)', foreground: 'hsl(222.2, 47.4%, 11.2%)' },
          card: { DEFAULT: 'hsl(0, 0%, 100%)', foreground: 'hsl(222.2, 84%, 4.9%)' },
        },
      },
    },
  };
</script>

<style>
  /* Reset for sandboxed iframe */
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; padding: 16px; font-family: ui-sans-serif, system-ui, sans-serif; }
  #root { min-height: 0; }
  #error-display {
    display: none;
    margin: 16px;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid hsl(0, 84.2%, 60.2%, 0.5);
    background: hsl(0, 84.2%, 60.2%, 0.1);
    color: hsl(0, 84.2%, 60.2%);
    font-size: 14px;
    font-family: ui-monospace, monospace;
    white-space: pre-wrap;
  }
</style>
</head>
<body>
<div id="root"></div>
<div id="error-display"></div>

<script type="module">
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sucrase from 'https://esm.sh/sucrase@3?bundle';

// ── Module registry ──
// Maps module specifiers to their exports. We pre-populate with the libraries
// listed in the import map so that Sucrase's CJS require() calls resolve.
const moduleRegistry = {};

// Eagerly load all import-mapped modules so they're available synchronously
// when the transpiled code calls require().
async function preloadModules() {
  const modules = {
    'react': () => import('react'),
    'react-dom': () => import('react-dom'),
    'react-dom/client': () => import('react-dom/client'),
    'recharts': () => import('recharts'),
    'lodash': () => import('lodash'),
    'lucide-react': () => import('lucide-react'),
  };

  await Promise.all(
    Object.entries(modules).map(async ([name, loader]) => {
      try {
        const mod = await loader();
        moduleRegistry[name] = mod;
      } catch (e) {
        console.warn('Failed to preload ' + name + ':', e);
      }
    })
  );

  // Also register 'react/jsx-runtime' since Sucrase may target it
  // (though we configure it not to, this is a safety net)
  moduleRegistry['react/jsx-runtime'] = moduleRegistry['react'];
}

// ── Simplified shadcn/ui components ──
// Pure React + Tailwind implementations (no Radix, no CVA).
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

function registerShadcnComponents() {
  const R = moduleRegistry['react'];

  // Card
  const Card = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { ref, className: cn('rounded-lg border border-border bg-card text-card-foreground shadow-sm', className), ...props })
  );
  const CardHeader = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { ref, className: cn('flex flex-col space-y-1.5 p-6', className), ...props })
  );
  const CardTitle = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { ref, className: cn('text-2xl font-semibold leading-none tracking-tight', className), ...props })
  );
  const CardDescription = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { ref, className: cn('text-sm text-muted-foreground', className), ...props })
  );
  const CardContent = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { ref, className: cn('p-6 pt-0', className), ...props })
  );
  const CardFooter = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { ref, className: cn('flex items-center p-6 pt-0', className), ...props })
  );

  // Button
  const buttonBase = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';
  const buttonVariants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
  };
  const buttonSizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  };
  const Button = R.forwardRef(({ className, variant = 'default', size = 'default', ...props }, ref) =>
    R.createElement('button', { ref, className: cn(buttonBase, buttonVariants[variant], buttonSizes[size], className), ...props })
  );

  // Badge
  const badgeBase = 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors';
  const badgeVariants = {
    default: 'border-transparent bg-primary text-primary-foreground',
    secondary: 'border-transparent bg-secondary text-secondary-foreground',
    destructive: 'border-transparent bg-destructive text-destructive-foreground',
    outline: 'text-foreground',
  };
  function Badge({ className, variant = 'default', ...props }) {
    return R.createElement('div', { className: cn(badgeBase, badgeVariants[variant], className), ...props });
  }

  // Table
  const Table = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('div', { className: 'relative w-full overflow-auto' },
      R.createElement('table', { ref, className: cn('w-full caption-bottom text-sm', className), ...props })
    )
  );
  const TableHeader = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('thead', { ref, className: cn('[&_tr]:border-b', className), ...props })
  );
  const TableBody = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('tbody', { ref, className: cn('[&_tr:last-child]:border-0', className), ...props })
  );
  const TableRow = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('tr', { ref, className: cn('border-b transition-colors hover:bg-muted/50', className), ...props })
  );
  const TableHead = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('th', { ref, className: cn('px-4 text-left align-middle font-medium text-muted-foreground', className), ...props })
  );
  const TableCell = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('td', { ref, className: cn('p-4 align-middle', className), ...props })
  );
  const TableCaption = R.forwardRef(({ className, ...props }, ref) =>
    R.createElement('caption', { ref, className: cn('mt-4 text-sm text-muted-foreground', className), ...props })
  );

  // Tabs (pure React state-based implementation, no Radix)
  const TabsContext = R.createContext({ value: '', onChange: () => {} });
  function Tabs({ defaultValue, value: controlledValue, onValueChange, className, children, ...props }) {
    const [internalValue, setInternalValue] = R.useState(defaultValue || '');
    const value = controlledValue !== undefined ? controlledValue : internalValue;
    const onChange = (v) => { setInternalValue(v); onValueChange?.(v); };
    return R.createElement(TabsContext.Provider, { value: { value, onChange } },
      R.createElement('div', { className, ...props }, children)
    );
  }
  function TabsList({ className, ...props }) {
    return R.createElement('div', { className: cn('inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground', className), ...props });
  }
  function TabsTrigger({ value, className, ...props }) {
    const ctx = R.useContext(TabsContext);
    const isActive = ctx.value === value;
    return R.createElement('button', {
      className: cn('inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all', isActive && 'bg-background text-foreground shadow-sm', className),
      onClick: () => ctx.onChange(value),
      ...props,
    });
  }
  function TabsContent({ value, className, children, ...props }) {
    const ctx = R.useContext(TabsContext);
    if (ctx.value !== value) return null;
    return R.createElement('div', { className: cn('mt-2', className), ...props }, children);
  }

  // Progress
  function Progress({ value = 0, className, ...props }) {
    return R.createElement('div', { className: cn('relative h-4 w-full overflow-hidden rounded-full bg-secondary', className), ...props },
      R.createElement('div', { className: 'h-full w-full flex-1 bg-primary transition-all', style: { transform: 'translateX(-' + (100 - (value || 0)) + '%)' } })
    );
  }

  // Separator
  function Separator({ orientation = 'horizontal', className, ...props }) {
    return R.createElement('div', {
      className: cn('shrink-0 bg-border', orientation === 'horizontal' ? 'h-[1px] w-full' : 'h-full w-[1px]', className),
      ...props,
    });
  }

  // Register as a virtual module
  moduleRegistry['@/components/ui'] = {
    Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
    Button, Badge,
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableCaption,
    Tabs, TabsList, TabsTrigger, TabsContent,
    Progress, Separator,
  };

  // Also register individual component paths for flexible imports
  moduleRegistry['@/components/ui/card'] = { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
  moduleRegistry['@/components/ui/button'] = { Button };
  moduleRegistry['@/components/ui/badge'] = { Badge };
  moduleRegistry['@/components/ui/table'] = { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableCaption };
  moduleRegistry['@/components/ui/tabs'] = { Tabs, TabsList, TabsTrigger, TabsContent };
  moduleRegistry['@/components/ui/progress'] = { Progress };
  moduleRegistry['@/components/ui/separator'] = { Separator };
}

// ── CJS require shim ──
// Sucrase's "imports" transform converts ESM imports to require() calls.
function require(specifier) {
  if (moduleRegistry[specifier]) return moduleRegistry[specifier];
  throw new Error('Module not found: ' + specifier + '. Available: ' + Object.keys(moduleRegistry).join(', '));
}

// ── Transpile and render ──
let root = null;

function showError(message) {
  const el = document.getElementById('error-display');
  el.textContent = message;
  el.style.display = 'block';
  document.getElementById('root').style.display = 'none';
}

function clearError() {
  const el = document.getElementById('error-display');
  el.style.display = 'none';
  document.getElementById('root').style.display = 'block';
}

function renderCode(code) {
  try {
    clearError();

    // Transpile JSX + TypeScript → JS with CJS require() calls
    const result = Sucrase.transform(code, {
      transforms: ['jsx', 'typescript', 'imports'],
      // Use React.createElement (classic runtime) so we don't need jsx-runtime
      jsxRuntime: 'classic',
      production: true,
    });

    // Execute the transpiled code in a function scope with require/exports
    const exports = {};
    const module = { exports };
    const fn = new Function('require', 'exports', 'module', 'React', result.code);
    fn(require, exports, module, moduleRegistry['react']);

    // Get the default export (the component to render)
    const Component = module.exports.default || module.exports;
    if (typeof Component !== 'function' && typeof Component !== 'object') {
      throw new Error('Module must export a React component as default export');
    }

    const R = moduleRegistry['react'];
    if (!root) {
      const { createRoot: cr } = moduleRegistry['react-dom/client'];
      root = cr(document.getElementById('root'));
    }

    root.render(R.createElement(Component));

    // Report height after render settles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: 'rendered', height }, '*');
      });
    });

  } catch (err) {
    console.error('Renderer shell error:', err);
    showError(err.message);
    window.parent.postMessage({ type: 'error', message: err.message }, '*');
  }
}

// ── postMessage listener ──
window.addEventListener('message', (event) => {
  if (event.data?.type === 'render' && typeof event.data.code === 'string') {
    renderCode(event.data.code);
  }
});

// ── Height observer ──
// Report height changes as the rendered content updates (e.g. after state changes,
// data loads, accordion expansions).
let lastReportedHeight = 0;
const resizeObserver = new ResizeObserver(() => {
  const height = document.body.scrollHeight;
  if (height !== lastReportedHeight) {
    lastReportedHeight = height;
    window.parent.postMessage({ type: 'rendered', height }, '*');
  }
});
resizeObserver.observe(document.getElementById('root'));

// ── Initialize ──
await preloadModules();
registerShadcnComponents();
window.parent.postMessage({ type: 'ready' }, '*');
</script>
</body>
</html>`;
